import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Link2, QrCode, Radio, Tag, Ticket, Copy, Loader2,
  ExternalLink, Plus, TrendingUp, Zap, Package, Share2,
  Instagram, Facebook, Youtube, Twitter, Send, MessageCircle
} from "lucide-react";
import authService from "../../services/authService";
import ConexaoTransmissao from "./ConexaoTransmissao";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/social`;

const SocialCommerce = () => {
  const usuarioId = authService.getUser()?.id || "";

  const [abaAtiva, setAbaAtiva] = useState("links");
  const [carregando, setCarregando] = useState(true);
  const [links, setLinks] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [campanhas, setCampanhas] = useState([]);

  // Painel de Live
  const [fornecedorIdLive, setFornecedorIdLive] = useState("");
  const [estoqueLive, setEstoqueLive] = useState([]);
  const [buscandoEstoque, setBuscandoEstoque] = useState(false);
  const [gerandoLink, setGerandoLink] = useState(null);

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const [respLinks, respDash, respCamp] = await Promise.all([
        authService.fetchAuth(`${API}/links/${usuarioId}`),
        authService.fetchAuth(`${API}/dashboard/${usuarioId}`),
        authService.fetchAuth(`${API}/campanhas/disponiveis`),
      ]);
      if (respLinks.ok) setLinks(await respLinks.json());
      if (respDash.ok) setDashboard(await respDash.json());
      if (respCamp.ok) setCampanhas(await respCamp.json());
    } catch (error) {
      console.error("Erro ao carregar social commerce:", error);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const copiarLink = (shortCode) => {
    const url = `${BACKEND_URL}/api/social/links/${shortCode}/click`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  };

  const buscarEstoqueLive = async () => {
    if (!fornecedorIdLive) {
      toast.error("Informe o ID do fornecedor.");
      return;
    }
    setBuscandoEstoque(true);
    try {
      // Usa o endpoint recomendado (produtos que combinam com o
      // historico do afiliado aparecem primeiro) - com fallback seguro
      // para o endpoint original caso algo falhe.
      const resp = await authService.fetchAuth(`${API}/live/estoque-recomendado/${fornecedorIdLive}/${usuarioId}`);
      if (resp.ok) {
        const data = await resp.json();
        setEstoqueLive(data.produtos || []);
      } else {
        const respOriginal = await authService.fetchAuth(`${API}/live/estoque/${fornecedorIdLive}`);
        if (!respOriginal.ok) throw new Error("Não foi possível buscar o estoque.");
        setEstoqueLive(await respOriginal.json());
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBuscandoEstoque(false);
    }
  };

  const venderAgora = async (produto) => {
    setGerandoLink(produto.id);
    try {
      const resp = await authService.fetchAuth(`${API}/live/vender-agora`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          afiliado_id: usuarioId,
          produto_id: produto.id,
          produto_nome: produto.nome,
          marketplace: "live",
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao gerar link");

      const url = `${BACKEND_URL}/api/social/links/${data.link.short_code}/click`;
      navigator.clipboard.writeText(url);
      toast.success(`Link gerado e copiado! "${data.mensagem_sugerida}"`);
      await carregarDados();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGerandoLink(null);
    }
  };

  const abas = [
    { id: "redes", label: "Redes Sociais", icon: Share2 },
    { id: "links", label: "Links Inteligentes", icon: Link2 },
    { id: "live", label: "Painel de Live", icon: Radio },
    { id: "campanhas", label: "Campanhas", icon: Tag },
  ];

  const REDES_SOCIAIS = [
    { id: "instagram", nome: "Instagram", icon: Instagram, cor: "text-pink-400" },
    { id: "facebook", nome: "Facebook", icon: Facebook, cor: "text-blue-500" },
    { id: "tiktok", nome: "TikTok", icon: Radio, cor: "text-gray-100" },
    { id: "whatsapp", nome: "WhatsApp", icon: MessageCircle, cor: "text-green-400" },
    { id: "telegram", nome: "Telegram", icon: Send, cor: "text-sky-400" },
    { id: "youtube", nome: "YouTube", icon: Youtube, cor: "text-red-500" },
    { id: "x", nome: "X (Twitter)", icon: Twitter, cor: "text-gray-300" },
  ];

  const compartilharUltimoLink = (rede) => {
    if (links.length === 0) {
      toast.error("Crie um link primeiro, na aba 'Links Inteligentes'.");
      return;
    }
    const ultimo = links[0];
    const url = `${BACKEND_URL}/api/social/links/${ultimo.short_code}/click`;
    const texto = encodeURIComponent(`Confira ${ultimo.produto_nome}! ${url}`);
    const links_compartilhar = {
      whatsapp: `https://wa.me/?text=${texto}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(ultimo.produto_nome)}`,
      x: `https://twitter.com/intent/tweet?text=${texto}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };
    if (links_compartilhar[rede]) {
      window.open(links_compartilhar[rede], "_blank");
    } else {
      navigator.clipboard.writeText(url);
      toast.success(`Link copiado! Cole no ${rede} para compartilhar.`);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Zap className="w-7 h-7 text-purple-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Social Commerce</h1>
          <p className="text-sm text-gray-400">Venda através das redes sociais - links, lives e campanhas</p>
        </div>
      </div>

      {dashboard && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-4 bg-gray-800/50 border-gray-700">
            <p className="text-xs text-gray-500">Total de Clicks</p>
            <p className="text-2xl font-bold text-white">{dashboard.total_clicks}</p>
          </Card>
          <Card className="p-4 bg-gray-800/50 border-gray-700">
            <p className="text-xs text-gray-500">Vendas Geradas</p>
            <p className="text-2xl font-bold text-green-400">{dashboard.total_vendas}</p>
          </Card>
          <Card className="p-4 bg-gray-800/50 border-gray-700">
            <p className="text-xs text-gray-500">Taxa de Conversão</p>
            <p className="text-2xl font-bold text-purple-400">{dashboard.taxa_conversao_percentual}%</p>
          </Card>
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b border-gray-700">
        {abas.map((aba) => {
          const Icone = aba.icon;
          return (
            <button
              key={aba.id}
              onClick={() => setAbaAtiva(aba.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                abaAtiva === aba.id ? "border-purple-500 text-purple-400" : "border-transparent text-gray-400 hover:text-white"
              }`}
            >
              <Icone className="w-4 h-4" />
              {aba.label}
            </button>
          );
        })}
      </div>

      {carregando && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando...
        </div>
      )}

      {/* REDES SOCIAIS */}
      {!carregando && abaAtiva === "redes" && (
        <div>
          <Card className="p-4 bg-purple-900/20 border-purple-700 mb-4">
            <p className="text-sm text-gray-300">
              Clique numa rede para compartilhar seu link mais recente diretamente nela.
              Gere um link novo na aba "Links Inteligentes" antes, se ainda não tiver nenhum.
            </p>
          </Card>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {REDES_SOCIAIS.map((rede) => {
              const Icone = rede.icon;
              return (
                <Card
                  key={rede.id}
                  onClick={() => compartilharUltimoLink(rede.id)}
                  className="p-4 bg-gray-800/50 border-gray-700 flex flex-col items-center gap-2 cursor-pointer hover:border-purple-500 transition-colors"
                >
                  <Icone className={`w-8 h-8 ${rede.cor}`} />
                  <p className="text-white text-sm font-medium">{rede.nome}</p>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* LINKS */}
      {!carregando && abaAtiva === "links" && (
        <div className="space-y-2">
          {links.length === 0 && (
            <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
              Nenhum link criado ainda. Gere links a partir dos seus produtos no Catálogo.
            </Card>
          )}
          {links.map((l) => (
            <Card key={l.id} className="p-4 bg-gray-800/50 border-gray-700 flex items-center justify-between">
              <div>
                <p className="text-white font-medium">{l.produto_nome}</p>
                <p className="text-xs text-gray-500">
                  {l.clicks} clicks · {l.vendas} vendas {l.marketplace ? `· ${l.marketplace}` : ""}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => copiarLink(l.short_code)}>
                <Copy className="w-3 h-3 mr-1" />
                Copiar link
              </Button>
            </Card>
          ))}
        </div>
      )}

      {/* PAINEL DE LIVE */}
      {!carregando && abaAtiva === "live" && (
        <div>
          <Card className="p-4 bg-purple-900/20 border-purple-700 mb-4">
            <p className="text-sm text-gray-300">
              <Radio className="w-4 h-4 inline mr-1 text-red-400" />
              Está ao vivo agora? Busque o estoque real do fornecedor e gere um link instantâneo
              para compartilhar no chat da live, sem sair da transmissão. Enquanto isso, o robô
              continua publicando sozinho nos marketplaces que não têm recurso de live.
            </p>
          </Card>

          <ConexaoTransmissao usuarioId={usuarioId} />

          <div className="flex gap-2 mb-4">
            <Input
              placeholder="ID do fornecedor"
              value={fornecedorIdLive}
              onChange={(e) => setFornecedorIdLive(e.target.value)}
              className="max-w-xs"
            />
            <Button onClick={buscarEstoqueLive} disabled={buscandoEstoque}>
              {buscandoEstoque ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Package className="w-4 h-4 mr-2" />}
              Ver estoque
            </Button>
          </div>

          <div className="space-y-2">
            {estoqueLive.map((p) => (
              <Card key={p.id} className="p-4 bg-gray-800/50 border-gray-700 flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{p.nome}</p>
                  <p className="text-xs text-gray-500">Estoque: {p.estoque} · R$ {(p.preco || 0).toFixed(2)}</p>
                </div>
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => venderAgora(p)}
                  disabled={gerandoLink === p.id}
                >
                  {gerandoLink === p.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Radio className="w-3 h-3 mr-1" />}
                  Vender agora
                </Button>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* CAMPANHAS */}
      {!carregando && abaAtiva === "campanhas" && (
        <div className="space-y-2">
          {campanhas.length === 0 && (
            <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
              Nenhuma campanha disponível no momento.
            </Card>
          )}
          {campanhas.map((c) => (
            <Card key={c.id} className="p-4 bg-gray-800/50 border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{c.nome}</p>
                  <p className="text-xs text-gray-500">{c.descricao}</p>
                </div>
                <span className="text-green-400 font-bold">{c.desconto_percentual}% OFF</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SocialCommerce;
